import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, AlertTriangle } from "lucide-react";

// Anglojęzyczne, byłe kolonie brytyjskie — priorytet dla Afryki Wschodniej
const EAST_AFRICA = ["Kenya", "Uganda", "Tanzania", "Rwanda", "South Sudan", "Zambia", "Malawi"];
const WIDER_AFRICA = ["Ghana", "Nigeria", "Botswana", "Mauritius", "Seychelles", "Zimbabwe", "Namibia", "Sierra Leone", "The Gambia", "Eswatini", "Lesotho"];

const FLAGS = {
  Kenya: "ke", Uganda: "ug", Tanzania: "tz", Rwanda: "rw", "South Sudan": "ss", Zambia: "zm", Malawi: "mw",
  Ghana: "gh", Nigeria: "ng", Botswana: "bw", Mauritius: "mu", Seychelles: "sc", Zimbabwe: "zw", Namibia: "na",
  "Sierra Leone": "sl", "The Gambia": "gm", Eswatini: "sz", Lesotho: "ls",
};

const pickCountry = () => {
  const pool = Math.random() < 0.75 ? EAST_AFRICA : WIDER_AFRICA;
  return pool[Math.floor(Math.random() * pool.length)];
};

export default function Login() {
  const [country] = useState(pickCountry);
  const location = useLocation();
  const authError = !!location.state?.authError;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Po odrzuconym logowaniu (niewłaściwa domena) pokaż komunikat i ponownie otwórz okno logowania.
  useEffect(() => {
    if (!authError) return;
    const t = setTimeout(handleLogin, 3000);
    return () => clearTimeout(t);
  }, [authError]);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left panel */}
      <div className="flex flex-col justify-between p-8 lg:p-16">
        <div className="flex items-center gap-2.5">
          <img src="/toren-mark.png" alt="Toren PATH" className="h-10 w-10 object-contain" />
          <span className="font-[Outfit] font-semibold text-lg tracking-tight">Toren <span className="text-[#C99734]">PATH</span> CRM</span>
        </div>

        <div className="max-w-md">
          <p className="text-sm font-[Manrope] uppercase tracking-widest text-[#C99734] mb-4">CRM Platform</p>
          <h1 className="font-[Outfit] font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mb-6">
            Łączymy Polskę z Afryką Wschodnią.
          </h1>
          <p className="text-base text-muted-foreground font-[Manrope] leading-relaxed mb-10">
            Dziś w centrum uwagi:{" "}
            <span className="inline-flex items-center gap-2 text-[#193962] font-semibold align-middle">
              Polska
              <img src="https://flagcdn.com/24x18/pl.png" srcSet="https://flagcdn.com/48x36/pl.png 2x" alt="Polska" className="h-4 w-auto rounded-[2px] shadow-sm" />
              &amp; {country}
              <img src={`https://flagcdn.com/24x18/${FLAGS[country]}.png`} srcSet={`https://flagcdn.com/48x36/${FLAGS[country]}.png 2x`} alt={country} className="h-4 w-auto rounded-[2px] shadow-sm" data-testid="country-flag" />
            </span>
            .<br /><br />Bezpieczne logowanie SSO dla zespołu Toren PATH — zaloguj się kontem Google Workspace, aby otworzyć swój regionalny pulpit.
          </p>
          {authError && (
            <div data-testid="auth-error-banner" className="mb-6 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-[Manrope] font-medium text-sm text-red-700">Brak dostępu dla tego konta.</p>
                <p className="font-[Manrope] text-sm text-red-600">Zaloguj się właściwym kontem służbowym. Za chwilę ponownie otworzymy okno logowania…</p>
              </div>
            </div>
          )}
          <Button
            onClick={handleLogin}
            data-testid="google-login-btn"
            className="h-12 px-6 bg-[#193962] hover:bg-[#12294A] text-white rounded-md font-[Manrope] font-medium transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md group"
          >
            <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
              <path fill="#fff" d="M12.24 10.29v3.62h5.05c-.21 1.31-1.5 3.85-5.05 3.85-3.04 0-5.52-2.52-5.52-5.62s2.48-5.62 5.52-5.62c1.73 0 2.89.74 3.55 1.37l2.42-2.33C16.66 3.6 14.68 2.7 12.24 2.7 7.42 2.7 3.5 6.62 3.5 11.44s3.92 8.74 8.74 8.74c5.05 0 8.39-3.55 8.39-8.55 0-.57-.06-1.01-.14-1.45l-8.25.11z"/>
            </svg>
            Continue with Google
            <ArrowRight className="h-4 w-4 ml-3 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>

        <p className="text-xs text-muted-foreground font-[Manrope]">© 2026 Toren PATH sp. z o.o. · Kraków</p>
      </div>

      {/* Right panel */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/4412921/pexels-photo-4412921.jpeg"
          alt="Modern office"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#193962]/40 to-black/20" />
      </div>
    </div>
  );
}
